import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config';
import { authenticatedFetch } from '../utils/api';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialize from localStorage immediately for instant UI update
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem('darkMode');
    if (stored !== null) {
      return stored === 'true';
    }
    // Fallback to system preference if no localStorage value
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  // Apply theme to DOM immediately on mount and whenever isDark changes
  useEffect(() => {
    // Set data-theme attribute for CSS variables
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    
    // Also set classes for backward compatibility
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
    
    // Save to localStorage immediately
    localStorage.setItem('darkMode', isDark.toString());
  }, [isDark]);

  // Load theme from API on mount (but don't override if localStorage exists)
  useEffect(() => {
    const loadThemeFromAPI = async () => {
      try {
        // Only fetch from API if we have a stored preference (user is likely logged in)
        // This prevents unnecessary API calls for non-authenticated users
        const hasStoredPreference = localStorage.getItem('darkMode') !== null;
        
        if (hasStoredPreference) {
          const response = await fetch(API_ENDPOINTS.PROFILE_USER, {
            method: 'GET',
            credentials: 'include',
          });

          if (response.ok) {
            const data = await response.json();
            if (data.user?.darkMode !== undefined) {
              const apiDarkMode = data.user.darkMode === 1;
              const localDarkMode = localStorage.getItem('darkMode') === 'true';
              
              // Only update if API value differs from localStorage (sync them)
              if (apiDarkMode !== localDarkMode) {
                setIsDark(apiDarkMode);
                localStorage.setItem('darkMode', apiDarkMode.toString());
              }
            }
          }
        }
      } catch (error) {
        // Silently fail - localStorage value will be used
        console.error('Error loading theme from API:', error);
      }
    };

    // Small delay to ensure other initialization is complete
    const timer = setTimeout(loadThemeFromAPI, 100);
    return () => clearTimeout(timer);
  }, []);

  const toggleDarkMode = async () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    localStorage.setItem('darkMode', newIsDark.toString());

    // Sync with API (non-blocking)
    try {
      await authenticatedFetch(API_ENDPOINTS.PROFILE_USER, {
        method: 'PUT',
        body: JSON.stringify({
          darkMode: newIsDark ? 1 : 0,
        }),
      });
    } catch (error) {
      console.error('Error updating theme in database:', error);
      // Theme is still saved in localStorage, so it will persist
    }
  };

  // Alias for backward compatibility
  const toggleTheme = toggleDarkMode;

  const value = {
    isDark,
    toggleDarkMode,
    toggleTheme, // Alias for backward compatibility
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

